// Global Instructions Rule Applied!
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../../state_manager/store.ts'

interface FileViewerProps {
  fileUrl?: string | null
  fileName?: string | null
}

/**
 * FileViewer Component
 * Displays a file in an iframe with security restrictions to prevent downloading
 */
function FileViewer({ fileUrl, fileName }: FileViewerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const embedRef = useRef<HTMLObjectElement>(null)
  const accessToken = useSelector((state: RootState) => state.auth?.accessToken)
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)

  // Check if file is a PDF
  const isPDF = useCallback(() => {
    if (!fileName) {
      // Also check URL for PDF content type
      if (fileUrl && fileUrl.toLowerCase().includes('.pdf')) {
        return true
      }
      return false
    }
    const extension = fileName.toLowerCase().split('.').pop()
    return extension === 'pdf'
  }, [fileName, fileUrl])

  // Fetch PDF as blob to handle authentication
  useEffect(() => {
    const pdfCheck = isPDF()
    if (pdfCheck && fileUrl && !pdfBlobUrl && !pdfError) {
      const fetchPDF = async () => {
        try {
          const headers: Record<string, string> = {}
          if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`
          }

          const response = await fetch(fileUrl, { headers })
          if (!response.ok) {
            throw new Error('Failed to fetch PDF')
          }

          const blob = await response.blob()
          const blobUrl = URL.createObjectURL(blob)
          setPdfBlobUrl(blobUrl)
        } catch (error) {
          console.error('Error fetching PDF:', error)
          setPdfError(error instanceof Error ? error.message : 'Failed to fetch PDF')
        }
      }

      fetchPDF()
    }

    // Cleanup blob URL on unmount
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl)
      }
    }
  }, [fileUrl, isPDF, accessToken, pdfBlobUrl, pdfError])

  useEffect(() => {
    // Prevent right-click context menu
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault()
      return false
    }

    // Prevent keyboard shortcuts for save/download
    // A keydown listener's return value is ignored by addEventListener; preventDefault does the blocking.
    const handleKeyDown = (e: KeyboardEvent) => {
      // Block Ctrl+S (Save)
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault()
        return
      }
      // Block Ctrl+P (Print)
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault()
        return
      }
      // Block Ctrl+Shift+S (Save As)
      if (e.ctrlKey && e.shiftKey && e.key === 'S') {
        e.preventDefault()
        return
      }
      // Block F12 (Developer Tools)
      if (e.key === 'F12') {
        e.preventDefault()
        return
      }
      // Block Ctrl+Shift+I (Developer Tools)
      if (e.ctrlKey && e.shiftKey && e.key === 'I') {
        e.preventDefault()
        return
      }
      // Block Ctrl+Shift+J (Console)
      if (e.ctrlKey && e.shiftKey && e.key === 'J') {
        e.preventDefault()
        return
      }
      // Block Ctrl+U (View Source)
      if (e.ctrlKey && e.key === 'u') {
        e.preventDefault()
        return
      }
    }

    // Prevent text selection
    const handleSelectStart = (e: Event) => {
      e.preventDefault()
      return false
    }

    // Prevent drag and drop
    const handleDragStart = (e: DragEvent) => {
      e.preventDefault()
      return false
    }

    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('selectstart', handleSelectStart)
    document.addEventListener('dragstart', handleDragStart)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('selectstart', handleSelectStart)
      document.removeEventListener('dragstart', handleDragStart)
    }
  }, [])

  if (!fileUrl) {
    return (
      <div className='flex items-center justify-center h-full'>
        <p className='text-gray-500'>No file URL provided</p>
      </div>
    )
  }

  // For PDFs, fetch as blob and display (handles authentication)
  if (isPDF()) {
    if (pdfError) {
      return (
        <div className='flex items-center justify-center h-full'>
          <p className='text-red-500'>Error loading PDF: {pdfError}</p>
        </div>
      )
    }

    if (!pdfBlobUrl) {
      return (
        <div className='flex items-center justify-center h-full'>
          <p className='text-gray-500'>Loading PDF...</p>
        </div>
      )
    }

    return (
      <div className='h-full w-full overflow-hidden' style={{ userSelect: 'none' }}>
        <object
          ref={embedRef}
          data={pdfBlobUrl}
          type='application/pdf'
          className='w-full h-full border-none'
          title={fileName || 'PDF Viewer'}
          style={{
            display: 'block',
            width: '100%',
            height: '100%'
          }}
        >
          <embed
            src={pdfBlobUrl}
            type='application/pdf'
            className='w-full h-full border-none'
            style={{
              display: 'block',
              width: '100%',
              height: '100%'
            }}
          />
        </object>
      </div>
    )
  }

  // For other files, use iframe
  return (
    <div className='h-full w-full overflow-hidden' style={{ userSelect: 'none' }}>
      <iframe
        ref={iframeRef}
        src={fileUrl}
        sandbox='allow-same-origin allow-scripts allow-popups allow-forms'
        allow='fullscreen'
        className='w-full h-full border-none'
        title={fileName || 'File Viewer'}
        style={{
          display: 'block',
          width: '100%',
          height: '100%'
        }}
      />
    </div>
  )
}

export default FileViewer
